import { forwardRef, useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { modeToText } from '@/utils/toTextFunctions';
import { FiAlertTriangle } from 'react-icons/fi';
import { timestampToJalali } from '@/utils/time';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import Flex from '@/components/common/Flex';
import TimePickerSpecific from '@/app/KeywordTracker/components/common/TimePickerSpecific';
import Select from 'react-select';
import type {
  FilterInputValue,
  FilterMode,
  FilterModeName,
  FilterSelectChangeHandler,
  FilterSelectOption,
  FilterSpecificationsProps,
  NumericKeyCounter
} from './filterTypes';

const isFilterSelectOption = (
  value: FilterInputValue | undefined
): value is FilterSelectOption =>
  typeof value === 'object' &&
  value !== null &&
  'label' in value &&
  'value' in value;

const getSelectLabel = (
  selectItems: FilterSpecificationsProps['selectItems'],
  field: string | undefined,
  value: FilterInputValue
) =>
  selectItems?.[field ?? '']?.find(selectItem => selectItem.value == value)
    ?.label ?? '';

const FilterSpecifications = forwardRef<
  NumericKeyCounter,
  FilterSpecificationsProps
>(({ columnValue, filterModes, setFilterModes, selectItems }, ref) => {
  const [filterValue, setFilterValue] = useState<FilterInputValue>();
  const [filterMode, setFilterMode] = useState<FilterModeName>();

  useEffect(() => {
    setTimeout(() => {
      resetFilters();
    }, 100);
  }, []);

  const resetFilters = () => {
    if (columnValue?.type === 'boolean') {
      setFilterMode('equals');
      setFilterValue(true);
    } else if (columnValue?.type === 'select') {
      setFilterMode('equals');
      setFilterValue(selectItems?.[columnValue.field ?? '']?.[0]);
    } else if (columnValue?.type === 'string') {
      setFilterMode('contains');
      setFilterValue('');
    } else if (columnValue?.type === 'number') {
      setFilterMode('equals');
      setFilterValue('0');
    } else if (columnValue?.type === 'array') {
      setFilterMode('contains');
      setFilterValue('');
    } else if (columnValue?.type === 'date') {
      setFilterMode('equals');
      // setFilterValue();
    }
  };
  const [onEdit, setOnEdit] = useState<FilterMode | null>(null);
  const submitEdit = () => {
    if (!onEdit || !filterMode) {
      return;
    }

    const copiedFilterModes = [...filterModes];
    const currentOnEditIndex = copiedFilterModes.findIndex(
      filter => filter.key === onEdit.key
    );
    if (currentOnEditIndex === -1) {
      return;
    }

    // اگر filterValue یک object باشد (برای select)، فقط value آن را بگیریم
    const actualValue = isFilterSelectOption(filterValue)
      ? filterValue.value
      : filterValue;
    copiedFilterModes[currentOnEditIndex] = {
      mode: filterMode,
      value: actualValue,
      key: copiedFilterModes[currentOnEditIndex].key
    };
    setFilterModes(copiedFilterModes);
    resetFilters();
    setOnEdit(null);
  };
  const addNewFilter = (
    mode: FilterModeName | undefined,
    value: FilterInputValue | undefined
  ) => {
    if (!mode) {
      return;
    }

    // اگر value یک object باشد (برای select)، فقط value آن را بگیریم
    const actualValue = isFilterSelectOption(value) ? value.value : value;
    const nextKey =
      ref && typeof ref !== 'function' && ref.current !== null
        ? ref.current++
        : filterModes.reduce(
            (maxKey, currentFilter) => Math.max(maxKey, currentFilter.key),
            0
          ) + 1;

    setFilterModes(prev => [
      ...prev,
      { mode, value: actualValue, key: nextKey }
    ]);
    resetFilters();
  };

  const deleteFilter = (key: number) => {
    if (key === onEdit?.key) setOnEdit(null);
    setFilterModes(prev => prev.filter(obj => obj.key !== key));
  };

  const inputValue =
    typeof filterValue === 'object' && filterValue !== null
      ? filterValue.value == null
        ? ''
        : String(filterValue.value)
      : typeof filterValue === 'boolean'
        ? String(filterValue)
        : (filterValue ?? '');
  const handleSelectChange: FilterSelectChangeHandler = value => {
    setFilterValue(value ?? undefined);
  };

  return (
    <div className="py-2">
      {filterModes.length <= 10 ? (
        <Row className="gy-3 gx-2">
          <Col sm={5}>
            {columnValue?.type === 'number' && (
              <Form.Select
                value={filterMode}
                onChange={e => setFilterMode(e.target.value as FilterModeName)}
                size="sm"
                aria-label="نوع فیلتر"
              >
                <option value="equals">برابر هست</option>
                <option value="notequals">برابر نیست</option>
                <option value="lessthan">کمتر از</option>
                <option value="lessthanorequals">کمتر یا مساوی </option>
                <option value="greaterthan">بزرگتر از</option>
                <option value="greaterthanorequals">بزرگتر یا مساوی</option>
                {/* <option value="inrange">درمحدوده</option> */}
                {/* <option value="blank">خالی باشه</option> */}
                {/* <option value="notblank">خالی نباشه</option> */}
              </Form.Select>
            )}
            {columnValue?.type === 'date' && (
              <Form.Select
                value={filterMode}
                onChange={e => setFilterMode(e.target.value as FilterModeName)}
                size="sm"
                aria-label="نوع فیلتر"
              >
                <option value="equals">برابر هست</option>
                <option value="notequals">برابر نیست</option>
                <option value="lessthan">کمتر از</option>
                <option value="lessthanorequals">کمتر یا مساوی </option>
                <option value="greaterthan">بزرگتر از</option>
                <option value="greaterthanorequals">بزرگتر یا مساوی</option>
              </Form.Select>
            )}
            {(columnValue?.type === 'string' ||
              columnValue?.type === 'array') && (
              <Form.Select
                value={filterMode}
                onChange={e => setFilterMode(e.target.value as FilterModeName)}
                size="sm"
                aria-label="نوع فیلتر"
              >
                <option value="contains">شامل باشه</option>
                <option value="notcontains">شامل نباشه</option>
                <option value="equals">برابر هست</option>
                <option value="notequals">برابر نیست</option>
                <option value="startswith">شروع با</option>
                <option value="endswith">پایان با</option>
                <option value="blank">خالی باشه</option>
                <option value="notblank">خالی نباشه</option>
              </Form.Select>
            )}
            {columnValue?.type === 'boolean' && (
              <Form.Select
                value={filterMode}
                onChange={e => {
                  setFilterMode(e.target.value as FilterModeName);
                }}
                style={{ minWidth: 120 }}
                size="sm"
                aria-label="نوع فیلتر"
              >
                <option value="equals">برابر هست</option>
                <option value="notequals">برابر نیست</option>
              </Form.Select>
            )}
            {columnValue?.type === 'select' && (
              <Form.Select
                value={filterMode}
                onChange={e => setFilterMode(e.target.value as FilterModeName)}
                size="sm"
                aria-label="نوع فیلتر"
              >
                <option value="equals">برابر هست</option>
                <option value="notequals">برابر نیست</option>
                <option value="blank">خالی باشه</option>
                <option value="notblank">خالی نباشه</option>
              </Form.Select>
            )}
          </Col>
          <Col sm={7}>
            <Flex
              justifyContent="between"
              alignItems="center"
              className="gap-2 w-100"
            >
              {columnValue?.type === 'number' && (
                <Form.Control
                  type="number"
                  size="sm"
                  name="specificationProperty"
                  placeholder="مقدار"
                  value={inputValue}
                  onChange={e => setFilterValue(e.target.value)}
                />
              )}
              {columnValue?.type === 'string' ||
              columnValue?.type === 'array' ? (
                filterMode !== 'blank' && filterMode !== 'notblank' ? (
                  <Form.Control
                    type="text"
                    size="sm"
                    name="specificationProperty"
                    placeholder="مقدار"
                    value={inputValue}
                    onChange={e => setFilterValue(e.target.value)}
                  />
                ) : (
                  <Form.Control
                    type="text"
                    size="sm"
                    style={{ visibility: 'hidden' }}
                    name="specificationProperty"
                    placeholder=""
                    value=""
                    disabled
                    // value={filterValue}
                    // onChange={e => setFilterValue(e.target.value)}
                  />
                )
              ) : null}
              {columnValue?.type === 'boolean' && (
                <Form.Select
                  size="sm"
                  name="specificationProperty"
                  value={
                    filterValue === true
                      ? 'true'
                      : filterValue === false
                        ? 'false'
                        : ''
                  }
                  onChange={e => {
                    setFilterValue(e.target.value);
                  }}
                >
                  <option value="true">بله</option>
                  <option value="false">خیر</option>
                </Form.Select>
              )}
              {columnValue?.type === 'select' &&
                (filterMode !== 'blank' && filterMode !== 'notblank' ? (
                  <Select<FilterSelectOption, false>
                    classNamePrefix="react-select"
                    closeMenuOnSelect={true}
                    placeholder="مقدار"
                    styles={{
                      control: baseStyles => ({
                        ...baseStyles,
                        width: '200px',
                        height: '30px',
                        cursor: 'pointer'
                      }),
                      input: baseStyles => ({
                        ...baseStyles,
                        color: 'var(--falcon-gray-700) !important',
                        boxShadow: 'none !important',
                        direction:
                          columnValue.field === 'landing_url' ? 'ltr' : 'rtl'
                      }),
                      option: baseStyles => ({
                        ...baseStyles,
                        cursor: 'pointer',
                        direction:
                          columnValue.field === 'landing_url' ? 'ltr' : 'rtl',
                        backgroundColor: 'transparent !important'
                      })
                    }}
                    value={filterValue as FilterSelectOption | null}
                    onChange={handleSelectChange}
                    options={selectItems?.[columnValue.field ?? ''] ?? []}
                    noOptionsMessage={() => 'موردی یافت نشد!!!'}
                  />
                ) : (
                  <Form.Control
                    type="text"
                    size="sm"
                    style={{ visibility: 'hidden' }}
                    name="specificationProperty"
                    placeholder=""
                    value=""
                    disabled
                  />
                ))}
              {columnValue?.type === 'date' && (
                <TimePickerSpecific
                  availableDays={undefined}
                  time={filterValue}
                  setTime={(value: FilterInputValue) => setFilterValue(value)}
                />
              )}
              {onEdit ? (
                <Button
                  variant="falcon-primary"
                  size="sm"
                  type="button"
                  onClick={submitEdit}
                >
                  ویرایش
                </Button>
              ) : (
                <ChildrensTooltip
                  placement="top"
                  html={
                    filterModes.length > 10
                      ? 'حداکثر ۱۰ فیلتر میتونی وارد کنی'
                      : (columnValue?.type === 'string' ||
                            columnValue?.type === 'array') &&
                          (filterMode === 'blank' || filterMode === 'notblank'
                            ? false
                            : !filterValue)
                        ? 'لطفا مقدار فیلتر رو وارد کنید'
                        : ''
                  }
                >
                  <Button
                    variant="falcon-primary"
                    size="sm"
                    type="button"
                    // disabled={
                    //   filterModes.length > 10 ||
                    //   ((columnValue?.type === 'string' ||
                    //     columnValue?.type === 'array') &&
                    //     (filterMode === 'blank' || filterMode === 'notblank'
                    //       ? false
                    //       : !filterValue))
                    // }
                    onClick={() => {
                      !(
                        filterModes.length > 10 ||
                        ((columnValue?.type === 'string' ||
                          columnValue?.type === 'array') &&
                          (filterMode === 'blank' || filterMode === 'notblank'
                            ? false
                            : !filterValue))
                      ) && addNewFilter(filterMode, filterValue);
                    }}
                  >
                    افزودن
                  </Button>
                </ChildrensTooltip>
              )}
            </Flex>
          </Col>
        </Row>
      ) : (
        <Alert variant="warning" className="mb-2 p-2">
          <FiAlertTriangle className="text-warning" /> نمی توانید بیش از 10
          فیلتر اضافه کنید‍‍‍!!!
        </Alert>
      )}

      {filterModes.length != 0 &&
        filterModes.map((filter, index) => (
          <Row key={index} className="gx-2 gy-3 my-1">
            <Col sm={5}>
              <Form.Control
                size="sm"
                disabled
                readOnly
                value={modeToText(filter.mode)}
              />
            </Col>
            <Col sm={7}>
              <div
                className="d-flex justify-content-between align-items-center w-100"
                style={{
                  gap: '0.57rem'
                }}
              >
                <Form.Control
                  style={{
                    visibility:
                      filter.mode === 'blank' || filter.mode === 'notblank'
                        ? 'hidden'
                        : undefined
                  }}
                  size="sm"
                  disabled
                  readOnly
                  value={
                    columnValue.type !== 'boolean' &&
                    columnValue.type !== 'select' &&
                    columnValue.type !== 'date'
                      ? filter.value == null
                        ? ''
                        : String(filter.value)
                      : columnValue.type == 'date'
                        ? timestampToJalali(filter.value)
                        : columnValue.type == 'boolean'
                          ? filter.value === true
                            ? 'بله'
                            : 'خیر'
                          : columnValue.type == 'select'
                            ? filter.mode !== 'blank' &&
                              filter.mode !== 'notblank'
                              ? getSelectLabel(
                                  selectItems,
                                  columnValue.field,
                                  filter.value
                                )
                              : ''
                            : (selectItems?.[columnValue.field ?? '']?.[0]
                                ?.label ?? '')
                  }
                />
                {onEdit && filter?.key === onEdit?.key ? (
                  <Button
                    onClick={() => submitEdit()}
                    variant="success"
                    className="d-flex gap-2 align-items-center"
                    style={{
                      padding: '0.4em',
                      visibility:
                        filter.mode === 'blank' || filter.mode === 'notblank'
                          ? 'hidden'
                          : undefined
                    }}
                  >
                    <FontAwesomeIcon size="sm" icon="check" />
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      setOnEdit(filter);
                      setFilterMode(filter.mode);
                      setFilterValue(filter.value);
                    }}
                    variant="falcon-primary"
                    className="d-flex gap-2 align-items-center"
                    style={{
                      padding: '0.4em',
                      visibility:
                        filter.mode === 'blank' || filter.mode === 'notblank'
                          ? 'hidden'
                          : undefined
                    }}
                  >
                    <FontAwesomeIcon size="sm" icon="pencil" />
                  </Button>
                )}
                <Button
                  onClick={() => deleteFilter(filter.key)}
                  variant="danger"
                  className="d-flex gap-2 align-items-center"
                  style={{ padding: '0.44em', height: 28.8 }}
                >
                  <FontAwesomeIcon icon="trash-alt" className="fs-0" />{' '}
                </Button>
              </div>
            </Col>
          </Row>
        ))}
    </div>
  );
});

export default FilterSpecifications;
